// ============================================================
// TeleWeb — Firebase Service Layer
// Jika env vars tiada → fallback ke Demo Mode
// ============================================================
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  onAuthStateChanged,
  updateProfile,
  type Auth,
  type User as FbUser,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import type { User, Site, Template, ValidationResult } from '../types';

// ─── Firebase Config ──────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'teleweb-app.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'teleweb-app',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'teleweb-app.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// Check if Firebase is properly configured
export const isFirebaseConfigured = !!(firebaseConfig.apiKey && firebaseConfig.appId);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let functions: Functions | null = null;

if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    functions = getFunctions(app, 'asia-southeast1');
  } catch (err) {
    console.warn('Firebase init failed, using demo mode:', err);
  }
}

export { auth, db, functions };

// ─── Auth Service ─────────────────────────────────────────────────────

export const authService = {
  onAuthChange(callback: (user: FbUser | null) => void) {
    if (!auth) return () => {};
    return onAuthStateChanged(auth, callback);
  },

  async signInWithEmail(email: string, password: string) {
    if (!auth) throw new Error('Firebase tidak dikonfigurasi');
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  },

  async registerWithEmail(email: string, password: string, displayName: string) {
    if (!auth) throw new Error('Firebase tidak dikonfigurasi');
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    return result.user;
  },

  async signInWithGoogle() {
    if (!auth) throw new Error('Firebase tidak dikonfigurasi');
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result.user;
  },

  async signOut() {
    if (!auth) return;
    await fbSignOut(auth);
  },
};

// ─── Firestore Service ────────────────────────────────────────────────

export const firestoreService = {
  async createOrGetUserProfile(fbUser: FbUser): Promise<User> {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const result = await httpsCallable<Record<string, never>, { profile: User }>(functions, 'createUserProfile')({});
    return { ...result.data.profile, uid: fbUser.uid };
  },

  async getUserSites(uid: string): Promise<Site[]> {
    if (!db) return [];
    const q = query(
      collection(db, 'sites'),
      where('ownerId', '==', uid),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        publishedAt: data.publishedAt?.toDate() || null,
        expiresAt: data.expiresAt?.toDate() || null,
        paidUntil: data.paidUntil?.toDate() || null,
        analytics: {
          ...data.analytics,
          lastViewAt: data.analytics?.lastViewAt?.toDate() || null,
        },
      } as Site;
    });
  },

  async getSite(siteId: string): Promise<Site | null> {
    if (!db) return null;
    const snap = await getDoc(doc(db, 'sites', siteId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      publishedAt: data.publishedAt?.toDate() || null,
      expiresAt: data.expiresAt?.toDate() || null,
      paidUntil: data.paidUntil?.toDate() || null,
    } as Site;
  },

  async updateSiteSecurity(siteId: string, security: Partial<Site['security']>) {
    if (!db) throw new Error('Firestore tidak dikonfigurasi');
    const updates: Record<string, unknown> = { updatedAt: serverTimestamp() };
    for (const [key, value] of Object.entries(security)) {
      updates[`security.${key}`] = value;
    }
    await updateDoc(doc(db, 'sites', siteId), updates);
  },

  async deleteSite(siteId: string) {
    if (!db) throw new Error('Firestore tidak dikonfigurasi');
    await deleteDoc(doc(db, 'sites', siteId));
  },

  async getTemplates(): Promise<Template[]> {
    if (!db) return [];
    const snap = await getDocs(collection(db, 'templates'));
    return snap.docs.map((d) => {
      const x = d.data();
      return { templateId: d.id, name: x.name || d.id, category: x.category || 'business',
        description: x.description || '', tags: x.tags || [], rating: x.rating || 0,
        usageCount: x.usageCount || 0, isPremium: !!x.isPremium, htmlContent: x.htmlContent || '',
        variables: x.variables || [] } as Template;
    });
  },
};

// ─── Cloud Functions Service ──────────────────────────────────────────

export type BillingSettings = {
  monthlyPriceMinor: number; extraSlotPriceMinor: number; qrImageUrl: string;
  paymentInstructions: string; alternatePaymentInstructions: string; payeeName: string;
};
export type PaymentRequest = {
  paymentRequestId: string; kind: string; siteId: string | null; status: string;
  expiresAt: number; amountMinor: number; paymentDetails: BillingSettings; paymentReference?: string | null;
};
export type BillingStatus = {
  canPublish: boolean; requiresPayment: boolean; paidUntil: number; daysRemaining: number;
  amountMinor: number; settings: BillingSettings | null; payment: PaymentRequest | null; serverNow: number;
};

export const functionsService = {
  async getBillingStatus(data: { siteId: string }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    return (await httpsCallable<typeof data, BillingStatus>(functions, 'getBillingStatus')(data)).data;
  },
  async createPaymentRequest(data: { siteId: string }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    return (await httpsCallable<typeof data, { payment: PaymentRequest; serverNow: number }>(functions, 'createPaymentRequest')(data)).data;
  },
  async submitPaymentRequest(data: { paymentRequestId: string; paymentReference: string; paymentMethod: 'qr' | 'other' }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    await httpsCallable(functions, 'submitPaymentRequest')(data);
  },
  async adminUploadPaymentQr(data: { base64: string; mimeType: string }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    return (await httpsCallable<typeof data, { qrImageUrl: string }>(functions, 'adminUploadPaymentQr')(data)).data;
  },
  async adminGetSite(data: { siteId: string }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    return (await httpsCallable<typeof data, { siteId: string; siteName: string; htmlContent: string; publicUrl: string | null; status: string }>(functions, 'adminGetSite')(data)).data;
  },
  async adminGetBillingSettings() { if (!functions) throw new Error('Functions tidak dikonfigurasi'); const r = await httpsCallable(functions, 'adminGetBillingSettings')({}); return r.data as { settings: BillingSettings | null }; },
  async adminUpsertTemplate(data: Record<string, unknown>) { if (!functions) throw new Error('Functions tidak dikonfigurasi'); const r = await httpsCallable(functions, 'adminUpsertTemplate')(data); return r.data as { templateId: string }; },
  async adminDeleteTemplate(data: { templateId: string }) { if (!functions) throw new Error('Functions tidak dikonfigurasi'); await httpsCallable(functions, 'adminDeleteTemplate')(data); },
  async adminListPayments() { if (!functions) throw new Error('Functions tidak dikonfigurasi'); const r = await httpsCallable(functions, 'adminListPayments')({}); return r.data as { requests: Array<Record<string, unknown>> }; },
  async approvePayment(data: { paymentRequestId: string; transactionReference: string }) { if (!functions) throw new Error('Functions tidak dikonfigurasi'); await httpsCallable(functions, 'approvePayment')(data); },
  async adminListUsers() { if (!functions) throw new Error('Functions tidak dikonfigurasi'); const r = await httpsCallable(functions, 'adminListUsers')({}); return r.data as { users: Array<{ uid: string; email: string; displayName: string; role: string; sites: Array<{ siteId: string; siteName: string; status: string; htmlSize: number; publicUrl?: string | null; paidUntil?: number; daysRemaining?: number }> }> }; },
  async adminDeleteSite(data: { siteId: string }) { if (!functions) throw new Error('Functions tidak dikonfigurasi'); await httpsCallable(functions, 'adminDeleteSite')(data); },
  async adminGetUsage() { if (!functions) throw new Error('Functions tidak dikonfigurasi'); const r = await httpsCallable(functions, 'adminGetUsage')({}); return r.data as { sites: number; htmlBytes: number; templates: number; measured: string }; },
  async adminListRenewals() { if (!functions) throw new Error('Functions tidak dikonfigurasi'); const r = await httpsCallable(functions, 'adminListRenewals')({}); return r.data as { renewals: Array<{ siteId: string; siteName: string; ownerId: string; status: string; paidUntil: number; daysRemaining: number }>; serverNow: number }; },
  async adminUpdateBillingSettings(data: Record<string, unknown>) { if (!functions) throw new Error('Functions tidak dikonfigurasi'); await httpsCallable(functions, 'adminUpdateBillingSettings')(data); },
  async validateHtml(html: string): Promise<ValidationResult> {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable<{ html: string }, ValidationResult>(functions, 'validateHtml');
    const result = await fn({ html });
    return result.data;
  },

  async publishSite(data: {
    siteId: string;
    siteName: string;
    htmlContent: string;
    templateId?: string;
    customSlug?: string;
  }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable(functions, 'publishSite');
    const result = await fn(data);
    return result.data as {
      success: boolean;
      publicUrl: string;
      customSlug?: string;
    };
  },

  async deleteSite(siteId: string) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable(functions, 'deleteSite');
    await fn({ siteId });
  },

  async updateSecurity(siteId: string, security: Record<string, unknown>) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable(functions, 'checkSiteSecurity');
    const result = await fn({ siteId, security });
    return result.data as { updated: boolean; securityScore: number };
  },

  async getSecurityLogs(siteId: string, limit = 50, page = 1) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable(functions, 'getSecurityLogs');
    const result = await fn({ siteId, limit, page });
    return result.data;
  },

  async getAnalytics(siteId: string, period = '7d') {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable(functions, 'getSiteAnalytics');
    const result = await fn({ siteId, period });
    return result.data;
  },

  async adminSetUserRole(data: { targetUserEmail: string; role: 'admin' | 'user' }) {
    if (!functions) throw new Error('Functions tidak dikonfigurasi');
    const fn = httpsCallable(functions, 'adminSetUserRole');
    const result = await fn(data);
    return result.data as { success: boolean };
  },

};
